import { motion } from 'framer-motion';

import { BaseTooltip } from '@/components/common/base-tooltip';
import { useFetchGlobalState } from '@/hooks/solana/query/use-fetch-global-state';
import { useFetchMarketState } from '@/hooks/solana/query/use-fetch-market-state';

type Props = {
  marketId: number;
  discount: number;
  spread: number;
  isMarketLimit: boolean;
  nextBestPrice: number | null;
  tradeType: 'buy' | 'sell';
};

const rotateWiggle = {
  animate: {
    rotate: [0, 20, -15, 0],
    transition: { duration: 0.5 },
  },
  style: {
    originX: 0,
    originY: 0,
  },
};

export const EventDiscount = ({
  marketId,
  discount,
  spread,
  isMarketLimit,
  nextBestPrice,
  tradeType,
}: Props) => {
  const { data: globalState } = useFetchGlobalState();

  const { data: marketState } = useFetchMarketState(marketId);
  const defaultTradeFee = marketState?.tradeFeePercent
    ? marketState.tradeFeePercent / 100
    : 0;

  if (defaultTradeFee === 0) {
    return null;
  }

  const liquidityIncentiveDiscountPerSpread =
    globalState?.liquidityIncentiveDiscountPerSpread
      ? globalState.liquidityIncentiveDiscountPerSpread / 100
      : 0;
  const maxSpread = globalState?.liquidityIncentiveMaxSpread ?? 0;

  return (
    <div className="">
      <BaseTooltip
        content={
          discount > 0 && !isMarketLimit ? (
            <div className="flex flex-col">
              <p>
                Limit orders within{' '}
                <span className="text-primary-yes font-bold">{maxSpread}¢</span>{' '}
                of the market price earn a discount.
              </p>
              <p>
                You’re within{' '}
                <span className="text-primary-yes font-bold">{spread}¢</span> —
                fee drops from{' '}
                <span className="text-primary-yes font-bold">
                  {defaultTradeFee}%
                </span>{' '}
                to{' '}
                <span className="text-primary-yes font-bold">
                  {defaultTradeFee -
                    (maxSpread - spread + 1) *
                      liquidityIncentiveDiscountPerSpread}
                  %.
                </span>
              </p>
            </div>
          ) : discount > 0 && isMarketLimit ? (
            <div className="flex flex-col">
              <p>
                Part of your order may execute immediately at the current market
                price and won’t qualify for a discount.
              </p>
              <p>
                Any remaining size placed within{' '}
                <span className="text-primary-yes font-bold">{maxSpread}¢</span>{' '}
                of the market price will earn a reduced fee.
              </p>
            </div>
          ) : (
            <div className="flex flex-col">
              <p>
                Limit orders within{' '}
                <span className="text-primary-yes font-bold">{maxSpread}¢</span>{' '}
                of the market price earn a discount.
              </p>
              {nextBestPrice && (
                <p>
                  Set your price between{' '}
                  <span className="text-primary-yes font-bold">
                    {tradeType === 'buy'
                      ? Math.max(nextBestPrice - 1, 1)
                      : Math.min(nextBestPrice + 1, 99)}
                    ¢
                  </span>{' '}
                  and{' '}
                  <span className="text-primary-yes font-bold">
                    {tradeType === 'buy'
                      ? Math.max(nextBestPrice - maxSpread, 1)
                      : Math.min(nextBestPrice + maxSpread, 99)}
                    ¢
                  </span>{' '}
                  to qualify.
                </p>
              )}
            </div>
          )
        }
      >
        <motion.img
          src={
            discount > 0 ? '/icons/discount.svg' : '/icons/discount-dark.svg'
          }
          alt="discount"
          className="size-6 sm:size-8"
          {...(discount > 0
            ? rotateWiggle
            : { style: { originX: 0, originY: 0 } })}
        />
      </BaseTooltip>
    </div>
  );
};
